import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BorderBox,
  GradientLine,
  Pill,
  TopicsSection,
  SegmentedControl,
  Spinner,
} from '../components/index.js';
import { LeftArrowCircleIcon, ChevronRightIcon } from '../assets/icons/index.js';
import helpCategories from '../data/helpCategories.js';
import requestHandler from '../utils/requestHandler.js';

export default function Help() {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('');
  const [mobileView, setMobileView] = useState('articles');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadArticles = async () => {
      setLoading(true);
      const res = await requestHandler({ path: 'articles', method: 'GET' });
      if (res.success) {
        const grouped = {};
        res.data.forEach((article) => {
          const catSlug = article.category.toLowerCase().replace(/\s+/g, '-');
          if (!grouped[catSlug]) {
            grouped[catSlug] = { categorySlug: catSlug, label: article.category, articles: [] };
          }
          grouped[catSlug].articles.push({ slug: article.slug, title: article.title, teaser: article.teaser });
        });
        const order = helpCategories.map((c) => c.index);
        const sorted = Object.values(grouped).sort((a, b) => {
          const ai = order.indexOf(a.categorySlug);
          const bi = order.indexOf(b.categorySlug);
          return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
        });
        setCategories(sorted);
      }
      setLoading(false);
    };
    loadArticles();
  }, []);

  // Check for navigation state (e.g. coming back from HelpArticle with a category selected)
  useEffect(() => {
    if (location.state?.selectedCategory) {
      setSelectedCategory(location.state.selectedCategory);
      if (window.innerWidth < 768) {
        setMobileView('categories');
      }
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const displayedCategories = selectedCategory
    ? categories.filter((c) => c.categorySlug === selectedCategory)
    : categories;

  const mobileOptions = [
    { name: 'articles', label: 'Articles' },
    { name: 'categories', label: 'Categories' },
  ];

  return (
    <div className="flex h-[calc(100vh-4rem)] md:h-[calc(100vh-4rem)] flex-row justify-between gap-4 pb-4">
      {/* Main Content */}
      <div className="flex flex-col gap-4 h-full overflow-y-auto no-scrollbar w-full md:w-3/4">
        <h1 className="text-4xl uppercase text-black dark:text-lz-concrete">
          <div className="flex flex-row gap-2 items-center">
            {selectedCategory && (
              <button
                onClick={() => {
                  setSelectedCategory('');
                  setMobileView('articles');
                }}
                aria-label="Back to all categories"
              >
                <LeftArrowCircleIcon className="dark:fill-lz-concrete w-10 h-10 cursor-pointer" />
              </button>
            )}
            Help
          </div>
        </h1>

        {selectedCategory && (
          <>
            <span className="text-2xl text-lz-ember dark:text-lz-candle-dim">
              {helpCategories.find((c) => c.index === selectedCategory)?.label}
            </span>
            <span className="text-lg text-lz-ink-mid dark:text-lz-concrete">
              {helpCategories.find((c) => c.index === selectedCategory)?.description}
            </span>
          </>
        )}

        {/* Mobile Segmented Control */}
        <div className="md:hidden">
          <SegmentedControl
            options={mobileOptions}
            selected={mobileView}
            onChange={setMobileView}
          />
        </div>

        {/* Mobile Categories Carousel */}
        {mobileView === 'categories' && (
          <div className="md:hidden w-full">
            <div className="overflow-x-auto no-scrollbar pb-2">
              <div className="flex flex-row gap-2 w-max">
                {helpCategories.map((cat) => (
                  <Pill
                    key={cat.index}
                    selected={selectedCategory === cat.index}
                    onClick={() => setSelectedCategory(cat.index)}
                    className="whitespace-nowrap text-sm"
                  >
                    {cat.label}
                  </Pill>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Category Cards */}
        {loading ? (
          <Spinner />
        ) : (
          <div className="flex flex-col gap-4">
            {displayedCategories.map((category) => (
              <BorderBox key={category.categorySlug} className="h-auto">
                <h2 className="text-2xl font-semibold text-lz-ink-mid dark:text-lz-concrete mb-2">
                  {category.label}
                </h2>
                <div className="flex flex-col">
                  {category.articles.map((article, i) => (
                    <div key={article.slug}>
                      {i > 0 && <GradientLine />}
                      <button
                        className="flex flex-row items-center justify-between w-full py-3 text-left group"
                        onClick={() => navigate(`/help/${article.slug}`)}
                      >
                        <div className="flex flex-col">
                          <span className="text-lg font-bold text-lz-ink-mid dark:text-lz-concrete group-hover:text-lz-ember dark:group-hover:text-lz-candle-dim transition-colors">
                            {article.title}
                          </span>
                          {article.teaser && (
                            <span className="text-sm text-lz-ink-mid/70 dark:text-lz-concrete mt-0.5">
                              {article.teaser}
                            </span>
                          )}
                        </div>
                        <ChevronRightIcon className="w-4 h-4 fill-lz-ink-mid dark:fill-lz-concrete flex-shrink-0 ml-2" />
                      </button>
                    </div>
                  ))}
                </div>
              </BorderBox>
            ))}
          </div>
        )}
      </div>

      {/* Desktop Categories Sidebar */}
      <TopicsSection
        onTopicClick={setSelectedCategory}
        topics={helpCategories}
        selectedTopic={selectedCategory}
        className="hidden md:block"
      />
    </div>
  );
}
